import {
  Cart,
  Attribute,
  ProductVariant,
  CartLine,
} from '@lambda/commons/apis/shopify/types/storefront';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { RC_DURATION } from '@lambda/reebelo';
import { Logger } from '@lambda/commons/logging/logger';
import { selectWarranty } from '@/lib/collections/catalogue/helpers';
import { CartUpdates, CartLineWithRCareSpecs } from './types';
import { CartItemAttribute } from '@/lib/use-cart';
import { CartTypes } from '.';

const logger = new Logger('cart-page');

type BundleItem = {
  prodName?: string;
  id: string;
  quantity: number;
  attributes: Attribute[];
  merchandise: ProductVariant;
};

export interface PricePayload {
  productId: number;
  variantId: number;
  price: number | string;
  sku: string;
}

function getIdFromGid(gid: string): number {
  const segments = gid.split('/');

  if (segments.length === 0) return 0;

  return Number(segments[segments.length - 1]);
}

export function reconcileEsAndShopfiyPrice(
  cart: Cart,
  pricePayload?: PricePayload,
): void {
  if (!pricePayload) return;

  const { productId, variantId, sku, price: rawLocalPrice } = pricePayload;
  const localPrice =
    typeof rawLocalPrice === 'string'
      ? parseFloat(rawLocalPrice)
      : rawLocalPrice;

  const mismatchNode = cart.lines?.edges
    .map((edge) => edge.node)
    .find((node) => {
      const shopifyPriceNum = parseFloat(node.merchandise.priceV2.amount);

      console.log(
        'ASBA',
        getIdFromGid(node.merchandise.id),
        variantId,
        getIdFromGid(node.merchandise.id) === variantId,
      );

      return (
        node.merchandise.sku === sku &&
        getIdFromGid(node.merchandise.id) === variantId &&
        shopifyPriceNum !== localPrice
      );
    });

  if (!mismatchNode) return;

  const shopifyPrice = parseFloat(mismatchNode.merchandise.priceV2.amount);
  const cartLineId = mismatchNode.id;
  const productTitle = mismatchNode.merchandise.product.title;

  logger.error(
    {
      shopifyProductId: productId,
      variantId,
      productTitle,
      sku,
      localStoragePrice: localPrice,
      shopifyPrice,
      cartLineId,
      cartId: cart.id,
    },
    'Price Mismatch',
  );
}

export const updateProdQtyWithRCareAndEShipping = (
  cart: Cart,
): CartUpdates[] => {
  if (!cart) return [];

  const edges = cart.lines?.edges;

  if (!Array.isArray(edges) || edges.length <= 1) return [];

  const getBundleItems = (type: 'reebelocare' | 'express-shipping') =>
    edges.reduce<Array<BundleItem>>((prev, edge) => {
      const {
        node: {
          attributes,
          merchandise: {
            product: { title },
          },
        },
      } = edge;

      const titleMatchingText =
        type === 'reebelocare' ? 'reebelocare' : 'express shipping';

      if (title.toLowerCase().includes(titleMatchingText)) {
        const forProd = attributes.filter(
          (attribute: Record<string, string>) =>
            attribute.key.toLowerCase() === 'for',
        );

        if (forProd && forProd.length > 0) {
          return [
            ...prev,
            {
              prodName: forProd[0].value,
              ...edge.node,
            },
          ];
        }

        return prev;
      }

      return prev;
    }, []);

  const getCartUpdateForBundle = (items: Array<BundleItem>) => {
    const updateList: Array<CartUpdates> = [];

    items.forEach((rcItem) => {
      const { prodName, quantity: rcItemQuantity } = rcItem;

      edges.forEach(({ node }) => {
        const {
          quantity,
          merchandise: {
            product: { title },
          },
        } = node;

        if (
          title.length !== prodName?.length &&
          prodName?.includes(title) &&
          quantity !== rcItemQuantity
        ) {
          updateList.push({
            attributes: rcItem.attributes as Array<CartItemAttribute>,
            id: rcItem.id,
            quantity,
          });
        }
      });
    });

    return updateList;
  };

  const reebeloCareItems = getBundleItems('reebelocare');
  const eShippingItems = getBundleItems('express-shipping');

  if (reebeloCareItems.length === 0 && eShippingItems.length === 0) return [];

  const updates: CartUpdates[] = [
    ...getCartUpdateForBundle(reebeloCareItems),
    ...getCartUpdateForBundle(eShippingItems),
  ];

  return updates;
};

export const enhancedKeySplit = (key: string) => {
  // Split the input key into an array of parts using '#' as the delimiter
  const keyParts = key.split('#');

  // Initialize a count to keep track of consecutive empty parts
  let consecutiveEmptyCount = 0;

  // Initialize a variable to hold the current non-empty part
  let currentPart = '';

  // Create an object to store the counts of consecutive empty parts for each key part
  const keyPartCounts: Record<string, number> = {};

  // Iterate through the key parts array
  keyParts.forEach((part) => {
    if (part.length > 0) {
      // If the part is not empty, update the currentPart and record the consecutive empty count
      currentPart = part;
      keyPartCounts[currentPart] = consecutiveEmptyCount;
      consecutiveEmptyCount = 0; // Reset the consecutive empty count
    } else {
      // If the part is empty, increment the consecutive empty count
      consecutiveEmptyCount += 1;
    }
  });

  // Filter out empty parts and map each non-empty part to its enhanced form
  return keyParts
    .filter(Boolean)
    .map((part) => '#'.repeat(keyPartCounts[part]) + part);
};

export const putRCareItemsNextToProduct = async (
  lineItems: CartLine[],
  warrantyEligiblityMap?: Record<string, boolean>,
): Promise<{
  rearrangedLineItems: CartLineWithRCareSpecs[];
  cartItemsWithoutRCare: CartLine[];
}> => {
  const [rCareItems, restCartItems] = lineItems.reduce(
    (prev, item) => {
      const lowercaseTitle = item.merchandise.product.title.toLowerCase();

      if (
        (lowercaseTitle.includes('reebelocare') &&
          !item.attributes.some((attr) => attr.key === 'PREVIOUS_ORDER_ID')) ||
        lowercaseTitle.includes('express shipping')
      )
        return [[...prev[0], item], [...prev[1]]];

      return [[...prev[0]], [...prev[1], item]];
    },
    [[], []] as Array<CartLine[]>,
  );

  const rCareItemsProdSkuMap: Record<
    string,
    Array<CartLineWithRCareSpecs>
  > = rCareItems.reduce((prev, { attributes, ...otherDetails }) => {
    const idAttr = attributes.find((attr) => attr.key === 'ID');

    if (!idAttr || !idAttr.value) return prev;

    const [, , rCareForProd] = enhancedKeySplit(idAttr.value);

    return {
      ...prev,
      [rCareForProd]: [
        ...(prev[rCareForProd] ? prev[rCareForProd] : []),
        {
          attributes,
          ...otherDetails,
        },
      ],
    };
  }, {} as Record<string, Array<CartLineWithRCareSpecs>>);

  const result = [];

  for (let index = 0; index < restCartItems.length; index += 1) {
    const item = restCartItems[index];
    const {
      merchandise: { sku },
    } = item;

    result.push(item);

    if (
      rCareItemsProdSkuMap[sku] != null &&
      rCareItemsProdSkuMap[sku].length !== 0
    ) {
      // eslint-disable-next-line no-await-in-loop
      const rCareLineItems = await Promise.all(
        rCareItemsProdSkuMap[sku].map(async (rCareItem) => {
          const {
            priceV2: { amount },
            product: { title, tags, productType },
          } = item.merchandise;

          const price = Number(amount);
          const { psku } = extractTags(tags);
          const productWarranty = warrantyEligiblityMap?.[psku || '']
            ? await selectWarranty(price, RC_DURATION['24_MONTH'], productType)
            : null;

          return {
            ...rCareItem,
            rCareSpecs: title.split('-')[0].trim(),
            rCarePrice: productWarranty?.price,
          };
        }),
      );

      result.push(...rCareLineItems);
    }
  }

  return {
    rearrangedLineItems: result,
    cartItemsWithoutRCare: restCartItems,
  };
};

export const generateCartItemsCount = (
  rearrangedLineItems: CartTypes.CartLineWithRCareSpecs[],
  includesTradeIn: boolean,
) => {
  const count = rearrangedLineItems.reduce(
    (prevCount, { quantity }) => prevCount + quantity,
    0,
  );

  if (includesTradeIn) return count + 1;

  return count;
};
